// /**
//  * Orders - list
//  */

// React
import React, {useContext, useState} from 'react';
import {Link} from 'react-router-dom';

// Context
import AuthContext from '../../../authContext';

// UI
import clsx from 'clsx';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
  lighten,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Checkbox,
  Typography,
  Tooltip,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Chip,
} from '@material-ui/core';

// Components
import Can from '../../../components/Can';

// Rules
import {
  rulesAdminChangeOrderStatus,
  rulesOrderStatus,
} from '../../../utils/rulesOrderStatus';

import {translate, checkMatchMedia} from '../../../utils/helpers';
import jMoment from 'moment-jalaali';

// Type
import {Order, Status} from '../../orders/interfaces';
import stableSort from '../../../utils/Table/stableSort';
import getComparator from '../../../utils/Table/getComparator';
import RonadIcons from '../../../components/iconComponent';

type Data = Order;
type Props = {
  data: any;
  handleConfirmCanceleBox: (id: number) => void;
  changeStatusOrderWhitId: any;
  handleOpenConfirmUpdateState: any;
};
type Ord = 'asc' | 'desc';

interface HeadCell {
  id: string;
  label: string;
  align?: string 
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      padding: '10px 0',

      backgroundColor: '#D5DADD !important',
      color: '#212121',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      padding: '10px 10px',
      borderRadius: '0',
      fontSize: 14,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  })
)(TableCell);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
    },
    table: {
      borderRadius: '4px',
      boxShadow: '0 0 24px -8px rgba(0,0,0,0.27)',
    },
    hover: {
      '&:hover': {
        backgroundColor: '#F8F9FA !important',
      },
    },
    fromRonad: {
      backgroundColor: '#b8c6e3',
      color: '#fff !important',
      '&:hover': {
        backgroundColor: '#8b99b5',
      },
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    columnText: {
      padding: '0 10px',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 750,
      borderRadius: '4px',
      boxShadow: '-4px 4px 12px 5px rgba(0,0,0,0.06)',
      border: '1px solid #d5dadd',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    iconButton: {
      padding: 10,
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        height: '36px',
        top: '4px',
        left: '-9px',
        backgroundColor: '#EDF1F4',
      },
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
  })
);

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      justifyContent: 'space-between',
    },
    highlight:
      theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      // flex: '1 1 100%',
    },
  })
);

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

interface EnhancedTableToolbarProps {
  numSelected: number;
  setOpenChangeStatus: any;
  selected: any;
  data: any;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const [openAlertWarning, setOpenAlertWarning] = useState(false as boolean);
  const {numSelected, selected, data} = props;

  const handleClickChangeStatus = () => {
    const selectedData = data.filter(
      (item: any) => selected.indexOf(item.id) > -1
    );

    if (!checkStatus(selectedData)) {
      setOpenAlertWarning(true);
    } else {
      props.setOpenChangeStatus(true);
    }
  };

  const checkStatus = (data: any) => {
    let sameStatus = true;
    data?.map((item: any) => {
      if (item?.status !== data[0]?.status) {
        return (sameStatus = false);
      }
      return item;
    });
    return sameStatus;
  };

  return (
    <>
      {numSelected > 0 && (
        <Toolbar
          className={clsx(classes.root, {
            [classes.highlight]: numSelected > 0,
          })}
        >
          {numSelected > 0 ? (
            <Typography
              className={classes.title}
              color="inherit"
              variant="subtitle1"
              component="div"
            >
              {numSelected} انتخاب
            </Typography>
          ) : (
            <Typography
              className={classes.title}
              variant="h6"
              id="tableTitle"
              component="div"
            />
          )}
          {numSelected > 0 ? (
            <Tooltip title="تغییر وضعیت">
              <Button
                size="small"
                variant="contained"
                onClick={handleClickChangeStatus}
                color="secondary"
              >
                تغییر وضعیت
              </Button>
            </Tooltip>
          ) : (
            <></>
            // <Tooltip title="Filter list">
            //   <IconButton aria-label="filter list">
            //     FilterIcon
            //   </IconButton>
            // </Tooltip>
          )}
        </Toolbar>
      )}
      <Dialog
        fullWidth
        open={openAlertWarning}
        onClose={() => setOpenAlertWarning(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>امکان تغییر وضعیت سفارش وجود ندارد </DialogTitle>
        <DialogContent>
          وضعیت سفارش آیتم‌های انتخاب شده باید یکی باشد
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => setOpenAlertWarning(false)}
            variant="contained"
            color="primary"
          >
            {'متوجه شدم'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ticketPriority = [
  {
    id: 0,
    name: 'LOW',
    title: 'کم',
    color: '#007644',
    bcolor: '#e8faea',
  },
  {
    id: 1,
    name: 'NORMAL',
    title: 'متوسط',
    color: '#FF7E07',
    bcolor: '#ffeedf',
  },
  {
    id: 2,
    name: 'HIGH',
    title: 'زیاد',
    color: '#FF0000',
    bcolor: '#fffafa',
  },
  {
    id: 3,
    name: 'CRITICAL',
    title: 'اضطراری',
    color: 'maroon',
    bcolor: '#fffafa',
  },
];

function EnhancedTableHead(props: EnhancedTableProps) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const {
    order,
    orderBy,
    onRequestSort,
    numSelected,
    rowCount,
    onSelectAllClick,
  } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  let headCells: HeadCell[] = [];

  headCells = [
    {
      id: 'tracking',
      label: 'شماره تیکت',
      align: 'center'
    },
    {
      id: 'linkId',
      label: 'شناسه',
      align: 'center'
    },
    {id: 'category', label: 'دسته‌بندی'},
    {id: 'subject', label: 'موضوع'},
    {id: 'title', label: 'عنوان'},
    {id: 'status', label: 'وضعیت'},
    {id: 'importance', label: 'دیده شدن'},
    {id: 'createdAt', label: 'تاریخ ایجاد', align: 'center'},
    {id: 'updatedAt', label: 'آخرین بروزرسانی', align: 'center'},
  ];

  return (
    <TableHead>
      <TableRow style={{borderRadius: '0'}}>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.align || "left"}
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <RonadIcons
                  name={
                    orderBy === headCell.id && order === 'desc'
                      ? 'chevronDown'
                      : 'chevronUp'
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              <span className={classes.columnText}>{headCell.label}</span>
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();

  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('updatedAt');
  const [selected, setSelected] = React.useState<string[]>([]);
  const [valueStatus, setValueStatus] = React.useState('');
  const [openChangeStatus, setOpenChangeStatus] = React.useState(false);

  const handleChangeValueStatus = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValueStatus((event.target as HTMLInputElement).value);
  };
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = props.data.map((n: any) => n.id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const changeStatusOrder = () => {
    props.changeStatusOrderWhitId({
      ids: selected,
      status: `${valueStatus}`,
      description: null,
    });
  };

  return (
    <div className={classes.root}>
      <EnhancedTableToolbar
        numSelected={selected.length}
        setOpenChangeStatus={setOpenChangeStatus}
        selected={selected}
        data={props.data}
      />
      <div>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
              numSelected={selected.length}
              onSelectAllClick={handleSelectAllClick}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const className =
                    row?.assignmentType == 'FFC'
                      ? classes.hover
                      : classes.fromRonad;

                  return (
                    <TableRow className={className} key={index}>
                      <StyledTableCell scope="row" align="center">
                        {row.tracking}
                      </StyledTableCell>
                      <StyledTableCell scope="row" align="center">
                        <Link
                          to={row.linkType === 'order' ? `/orders/${row.linkId}` : `/parcels/details/${row.linkId}`}
                        >
                          {row.linkId}
                        </Link>
                      </StyledTableCell>
                      <StyledTableCell data-cy="orderId">
                        {row.category.name}
                      </StyledTableCell>
                      <StyledTableCell data-cy="orderId">
                        {row.subject.body}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                      <HtmlTooltip title={`${row?.title}`}>
                        <span>{row.title && row?.title.substring(0, 40)}</span>
                      </HtmlTooltip>
                      </StyledTableCell>
                      <StyledTableCell align="left" data-cy="status">
                        {translate(`${row?.assignee?.type}_${row.status}`, 'ticket')}
                      </StyledTableCell>

                      <StyledTableCell>
                        <Chip
                          icon={
                            <RonadIcons
                              name="circleMedium"
                              size={0.6}
                              style={{
                                color: row.seen === '0' ? '#FF7E07' : '#007644',
                              }}
                            />
                          }
                          label={
                            row.seen === '1' ? 'دیده شده' : 'دیده نشده'
                          }
                          style={{
                            color: row.seen === '0' ? '#FF7E07' : '#007644',
                            backgroundColor: row.seen === '0' ? '#ffeedf' : '#e8faea',
                            fontSize: '16px',
                          }}
                        />
                      </StyledTableCell>

                      <StyledTableCell align="center">
                        <span>
                          {`${jMoment(row.createdAt.split('T')[1], 'HH:mm:ss')
                            .add(3, 'hours')
                            .add(30, 'minutes')
                            .locale('fa')
                            .format('HH:mm')}
                            `}
                        </span>
                        &nbsp;- &nbsp;
                        <span>
                          {jMoment(row.createdAt, 'YYYY/M/D')
                            .locale('fa')
                            .format('jYYYY/jMM/jDD')}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <span>
                          {`${jMoment(row?.updatedAt?.split('T')[1], 'HH:mm:ss')
                            .add(3, 'hours')
                            .add(30, 'minutes')
                            .locale('fa')
                            .format('HH:mm')}
                            `}
                        </span>
                        &nbsp;- &nbsp;
                        <span>
                          {jMoment(row.updatedAt, 'YYYY/M/D')
                            .locale('fa')
                            .format('jYYYY/jMM/jDD')}
                        </span>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Link
                          to={{
                            pathname: `/tickets/${row.id}`,
                            state: {
                              myProps: props,
                              myTicketId: row.id,
                            },
                          }}
                        >
                        <RonadIcons name="documentOutline" className='text-green'/>
                        </Link>
                      </StyledTableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <Dialog
        fullWidth
        open={openChangeStatus}
        onClose={() => setOpenChangeStatus(false)}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle> تغییر وضعیت کلی سفارش</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset">
            <FormLabel component="legend">وضعیت</FormLabel>
            <RadioGroup
              aria-label="status"
              name="Status"
              value={valueStatus}
              onChange={handleChangeValueStatus}
            >
              {rulesOrderStatus[
                props.data.find((data: any) => data.id === selected[0])?.status
              ]?.map((item: Status, index: number) => (
                <FormControlLabel
                  value={item}
                  key={index}
                  control={<Radio key={index} />}
                  label={translate(item, 'order')}
                  disabled={
                    rulesAdminChangeOrderStatus[authContext.user.role].indexOf(
                      item
                    ) === -1
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            onClick={() => setOpenChangeStatus(false)}
            variant="contained"
            color="primary"
          >
            {'خیر'}
          </Button>

          <Button
            onClick={changeStatusOrder}
            color="primary"
            variant="outlined"
          >
            {'تغییر وضعیت'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
